import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";

import MagnifierIcon from "@app/postBill/assets/svg/magnifier-icon.svg";
import { IntlContext } from "@app/provider";
import { getFormattedLocalizeDate } from "@app/utils";
import { PaymentMethod, TestName } from "@app/postBill/models";
import {
  convertBalance,
  getLocalizedTestFullName,
  maskedPaymentMethod,
} from "@app/postBill/utils";
import StatusPaid from "@app/postBill/components/statusPaid";

import "../billingStatementCard/billingStatementCard.scss";
import "./billingPaymentHistoryCard.scss";

const messages = defineMessages({
  billingPaymentHistoryCardViewReceipt: {
    id: "billingPaymentHistoryCardViewReceipt",
    defaultMessage: "View receipt",
  },
  billingPaymentHistoryCardDateOfService: {
    id: "billingPaymentHistoryCardDateOfService",
    defaultMessage: "Date of service",
  },
  billingPaymentHistoryCardCaseNumber: {
    id: "billingPaymentHistoryCardCaseNumber",
    defaultMessage: "Case number",
  },
  billingPaymentHistoryCardPaymentMethod: {
    id: "billingPaymentHistoryCardPaymentMethod",
    defaultMessage: "Payment method",
  },
});

interface BillingPaymentsHistoryProps {
  testName: TestName;
  caseNumber?: string;
  amount: number;
  currency: string;
  dateOfService: string;
  paidDate?: string;
  paymentMethod?: PaymentMethod;
  receiptId?: string;
  onViewReceipt: () => void;
}

const BillingHistoryPaymentCard: FC<BillingPaymentsHistoryProps> = ({
  testName,
  caseNumber,
  amount,
  currency,
  dateOfService,
  paidDate,
  paymentMethod,
  receiptId,
  onViewReceipt,
}) => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);

  return (
    <div className="billing-history-card" data-testid="payment-card">
      <div className="billing-history-card__display">
        <div className="billing-history-card__header">
          <div className="billing-history-card__title">
            {getLocalizedTestFullName(testName, intl)}
          </div>
          <div className="billing-history-card__amount">
            {convertBalance(amount, true, currency)}
          </div>
        </div>
        <div className="billing-history-card__status">
          <StatusPaid
            date={getFormattedLocalizeDate(paidDate, currentLanguage)}
          />
        </div>
        <div className="billing-history-card__content">
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(messages.billingPaymentHistoryCardCaseNumber)}
            </span>
            <span className="billing-history-card__value">{caseNumber}</span>
          </div>
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(
                messages.billingPaymentHistoryCardDateOfService
              )}
            </span>
            <span className="billing-history-card__value">
              {getFormattedLocalizeDate(dateOfService, currentLanguage)}
            </span>
          </div>
          {paymentMethod && (
            <div className="billing-history-card__row">
              <span className="billing-history-card__label">
                {intl.formatMessage(
                  messages.billingPaymentHistoryCardPaymentMethod
                )}
              </span>
              <span className="billing-history-card__value">
                {maskedPaymentMethod(paymentMethod)}
              </span>
            </div>
          )}
        </div>
        {receiptId && (
          <button
            className="billing-history-card__button"
            onClick={onViewReceipt}
          >
            <Svg
              content={MagnifierIcon}
              className="billing-history-card__button-icon"
            />
            {intl.formatMessage(messages.billingPaymentHistoryCardViewReceipt)}
          </button>
        )}
      </div>
    </div>
  );
};

export default BillingHistoryPaymentCard;
